import React, { useState, useEffect } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { CountryRoute } from './CountryDisplay';
import { Alert, AlertDescription } from './ui/alert';
import {
  Plane,
  Clock,
  AlertTriangle,
  CheckCircle2,
  RefreshCw,
  Calendar,
  Package
} from 'lucide-react';

const ShipmentMonitoring = () => {
  const { t, language } = useLanguage();
  const [monitoring, setMonitoring] = useState({
    delayed_shipments: [],
    arriving_soon: [],
    in_transit: [],
    total_delayed: 0,
    total_arriving_24h: 0,
    total_in_transit: 0
  });
  const [cronStatus, setCronStatus] = useState({
    cron_status: 'loading',
    cron_running: false,
    flight_api_enabled: false,
    flight_api_usage: null
  });
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState('');

  const backendUrl = process.env.REACT_APP_BACKEND_URL;

  const fetchMonitoringData = async () => {
    try {
      const token = localStorage.getItem('token');

      const monitoringResponse = await fetch(`${backendUrl}/api/shipments/monitoring`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (monitoringResponse.ok) {
        const monitoringData = await monitoringResponse.json();
        setMonitoring(monitoringData);
      }

      const cronResponse = await fetch(`${backendUrl}/api/cron/status`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (cronResponse.ok) {
        const cronData = await cronResponse.json();
        setCronStatus(cronData);
      }

    } catch (err) {
      setError(t('monitoringLoadError'));
      console.error('Error fetching monitoring data:', err);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const runManualCheck = async () => {
    try {
      setRefreshing(true);
      const token = localStorage.getItem('token');

      const response = await fetch(`${backendUrl}/api/cron/run-checks`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        setTimeout(() => {
          fetchMonitoringData();
        }, 2000);
      } else {
        setError(t('manualCheckRunError'));
      }
    } catch (err) {
      setError(t('manualCheckError'));
      console.error('Error running manual check:', err);
    }
  };

  const runFlightCheck = async () => {
    try {
      setRefreshing(true);
      const token = localStorage.getItem('token');

      const response = await fetch(`${backendUrl}/api/flights/check-shipments`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        setTimeout(() => {
          fetchMonitoringData();
        }, 3000);
      } else {
        const errorData = await response.json();
        setError(`${t('flightApiError')}: ${errorData.detail || t('serviceUnavailable')}`);
      }
    } catch (err) {
      setError(t('flightCheckError'));
      console.error('Error running flight check:', err);
    }
  };

  useEffect(() => {
    fetchMonitoringData();

    const interval = setInterval(fetchMonitoringData, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  const formatDate = (dateString) => {
    if (!dateString) return t('notDefined');
    try {
      return new Date(dateString).toLocaleString(language === 'fr' ? 'fr-FR' : 'en-US', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return dateString;
    }
  };

  const getStatusBadgeColor = (status) => {
    switch (status) {
      case 'scheduled': return 'bg-accent/10 text-accent';
      case 'in_transit': return 'bg-yellow-100 text-yellow-800';
      case 'arrived': return 'bg-green-100 text-green-800';
      case 'delayed': return 'bg-red-100 text-red-800';
      case 'completed': return 'bg-muted text-foreground';
      default: return 'bg-muted text-foreground';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <RefreshCw className="h-6 w-6 animate-spin mr-2" />
        <span>{t('loadingMonitoring')}</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header avec statut du cron et actions */}
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold">{t('shipmentMonitoring')}</h2>
        <div className="flex items-center gap-4">
          {/* Statut du service automatique */}
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <span className="text-sm text-muted-foreground">{t('cronJobStatus')}:</span>
              <Badge className={cronStatus.cron_running ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}>
                {cronStatus.cron_running ? `🔄 ${t('cronActive')}` : `⏸️ ${t('cronStopped')}`}
              </Badge>
            </div>

            <div className="flex items-center gap-2">
              <span className="text-sm text-muted-foreground">{t('flightApiStatus')}:</span>
              <Badge className={cronStatus.flight_api_enabled ? 'bg-accent/10 text-accent' : 'bg-muted text-foreground'}>
                {cronStatus.flight_api_enabled ? '✈️ ' + t('flightApiEnabled') : '❌ ' + t('flightApiDisabled')}
              </Badge>
            </div>
          </div>

          <div className="flex gap-2">
            <Button
              onClick={runManualCheck}
              disabled={refreshing}
              size="sm"
              variant="outline"
            >
              {refreshing ? (
                <RefreshCw className="h-4 w-4 animate-spin mr-2" />
              ) : (
                <RefreshCw className="h-4 w-4 mr-2" />
              )}
              {t('runManualCheck')}
            </Button>

            {cronStatus.flight_api_enabled && (
              <Button
                onClick={runFlightCheck}
                disabled={refreshing}
                size="sm"
                variant="outline"
                className="border-accent/20 text-accent hover:bg-accent/10"
              >
                <Plane className="h-4 w-4 mr-2" />
                {t('realTimeTracking')}
              </Button>
            )}
          </div>
        </div>
      </div>

      {error && (
        <Alert variant="destructive">
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {/* Statistiques globales */}
      <div className="grid md:grid-cols-3 gap-4">
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-red-100 rounded-full">
                <AlertTriangle className="h-6 w-6 text-red-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-red-600">{monitoring.total_delayed}</p>
                <p className="text-sm text-muted-foreground">{t('delayedShipments')}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-yellow-100 rounded-full">
                <Clock className="h-6 w-6 text-yellow-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-yellow-600">{monitoring.total_arriving_24h}</p>
                <p className="text-sm text-muted-foreground">{t('arrivingSoon')} (24h)</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-accent/10 rounded-full">
                <Plane className="h-6 w-6 text-accent" />
              </div>
              <div>
                <p className="text-2xl font-bold text-accent">{monitoring.total_in_transit}</p>
                <p className="text-sm text-muted-foreground">{t('inTransitShipments')}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Départs en retard */}
      {monitoring.delayed_shipments.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-red-600">
              <AlertTriangle className="h-5 w-5" />
              {t('delayedShipments')} ({monitoring.delayed_shipments.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {monitoring.delayed_shipments.map((shipment) => (
                <div key={shipment.id} className="border border-border rounded-lg p-4 bg-red-500/10">
                  <div className="flex justify-between items-start">
                    <div>
                      <h4 className="font-semibold text-red-900">{shipment.flight_name}</h4>
                      <p className="text-sm text-red-700">
                        <CountryRoute origin={shipment.origin_country} destination={shipment.destination_country} size="sm" />
                      </p>
                      <p className="text-xs text-red-600 mt-1">
                        {t('expectedArrival')}: {formatDate(shipment.expected_arrival_date)}
                      </p>
                    </div>
                    <div className="text-right">
                      <Badge className="bg-red-100 text-red-800 mb-2">
                        {t(shipment.status)}
                      </Badge>
                      <div className="text-xs text-red-600">
                        {shipment.package_count} {t('packages').toLowerCase()}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Arrivées dans les 24h */}
      {monitoring.arriving_soon.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-yellow-600">
              <Clock className="h-5 w-5" />
              {t('arrivingSoon')} - 24h ({monitoring.arriving_soon.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {monitoring.arriving_soon.map((shipment) => (
                <div key={shipment.id} className="border border-border rounded-lg p-4 bg-yellow-500/10">
                  <div className="flex justify-between items-start">
                    <div>
                      <h4 className="font-semibold text-yellow-900">{shipment.flight_name}</h4>
                      <p className="text-sm text-yellow-700">
                        <CountryRoute origin={shipment.origin_country} destination={shipment.destination_country} size="sm" />
                      </p>
                      <p className="text-xs text-yellow-600 mt-1">
                        {t('expectedArrival')}: {formatDate(shipment.expected_arrival_date)}
                      </p>
                    </div>
                    <div className="text-right">
                      <Badge className="bg-yellow-100 text-yellow-800 mb-2">
                        {t(shipment.status)}
                      </Badge>
                      <div className="text-xs text-yellow-600">
                        {shipment.package_count} {t('packages').toLowerCase()}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Départs en transit */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Plane className="h-5 w-5" />
            {t('inTransitShipments')} ({monitoring.in_transit.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {monitoring.in_transit.length > 0 ? (
            <div className="space-y-3">
              {monitoring.in_transit.map((shipment) => (
                <div key={shipment.id} className="border rounded-lg p-4">
                  <div className="flex justify-between items-start">
                    <div>
                      <h4 className="font-semibold">{shipment.flight_name}</h4>
                      <p className="text-sm text-muted-foreground">
                        <CountryRoute origin={shipment.origin_country} destination={shipment.destination_country} size="sm" />
                      </p>
                      <p className="text-xs text-muted-foreground mt-1">
                        {t('expectedArrival')}: {formatDate(shipment.expected_arrival_date)}
                      </p>
                    </div>
                    <div className="text-right">
                      <Badge className={getStatusBadgeColor(shipment.status)}>
                        {t(shipment.status)}
                      </Badge>
                      <div className="text-xs text-muted-foreground mt-1">
                        {shipment.package_count} {t('packages').toLowerCase()}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-muted-foreground">
              <Plane className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>{t('noTransitShipments')}</p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default ShipmentMonitoring;
